import React, { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  API,
  defaultLimitPagination,
  get,
  TableClasses,
  TableWrapClasses,
  TDClasses,
  THClasses,
  TRClasses,
} from "../../config";
import { debounce } from "lodash";
import MainBody from "../../components/MainBody/MainBody";
import BootstrapTable from "react-bootstrap-table-next";
import Pagination from "../../components/Pagination";

import { useTrust } from "../../lib/context/TrustContext";
import {
  formatNumber,
  formatSubcontractorName,
  getModuleNamingBySlug,
} from "../../lib/Misc";
import { CaretDownIcon, CaretRightIcon } from "@radix-ui/react-icons";
import DownloadABAActionButton from "./DownloadABAActionButton";
import Payments from "../Payments/Payments";
import { useOrganization } from "../../lib/context/OrganisationContext";

const DownloadedABA = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const search = query.get("search");
  const defaultPage = query.get("page");
  const defaultLimit = query.get("limit");
  const [abaList, setAbasList] = useState([]);
  const [loading, setLoading] = useState(false);

  const [sortField, setSortField] = useState();
  const [sortOrder, setSortOrder] = useState();
  const [searchParam, setSearchParam] = useState(search || "");
  const [searchParamData, setSearchParamData] = useState(search || "");
  const [page, setPage] = useState(defaultPage || 1);
  const [limit, setLimit] = useState(defaultLimit || defaultLimitPagination);
  const [total, setTotal] = useState();
  const [from, setFrom] = useState();
  const [to, setTo] = useState();

  const { trustId } = useTrust();

  const { organizationData } = useOrganization();

  const fetchAbas = useCallback(
    async (passLimit, passPage) => {
      if (trustId) {
        try {
          setLoading(true);
          const { data } = await get(
            `${API.GET_ABA_LIST_DOWNLOAD}?limit=${
              passLimit ? passLimit : limit ? limit : defaultLimitPagination
            }&page=${passPage ? passPage : page ? page : 1}&search=${
              searchParam ? searchParam : ""
            }&sort_column=${sortField ? sortField : ""}&sort_order=${
              sortOrder ? sortOrder : ""
            }&trust_id=${trustId ? trustId : ""}`,
          );

          setLoading(false);
          setLimit(data?.data?.per_page);
          setPage(data?.data?.current_page);
          setTotal(data?.data?.total);
          setFrom(data?.data?.from);
          setTo(data?.data?.to);
          const abaList = data.data.data;
          setAbasList(abaList);
          return data.data;
        } catch (error) {
          setLoading(false);
          setAbasList([]);
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [searchParam, sortOrder, sortField, trustId],
  );

  const abasGenerator = (quantity) => {
    const items = [];
    for (let i = 0; i < quantity; i++) {
      items.push({
        key: abaList[i]?.id,
        id: abaList[i]?.id,
        aba_file_id: abaList[i]?.aba_file_id,
        transaction_date: abaList[i]?.transaction_date
          ? abaList[i]?.transaction_date
          : "",
        name: abaList[i]?.multiple_payments
          ? `Multiple Payee`
          : abaList[i]?.contacts[0]
            ? `${formatSubcontractorName(
                abaList[i]?.contacts[0],
              )} - (${getModuleNamingBySlug(
                organizationData?.organizationData,
                "contacts",
                "C",
              )?.charAt(0)})`
            : `${formatSubcontractorName(
                abaList[i]?.vendors[0],
              )} - (${getModuleNamingBySlug(
                organizationData?.organizationData,
                "vendors",
                "V",
              )?.charAt(0)})`,
        bank_name: abaList[i]?.bank_name ? abaList[i]?.bank_name : "-",
        description: abaList[i]?.description ? abaList[i]?.description : "-",
        exc_gst_total_amount: abaList[i]?.total_amount
          ? "$" +
            formatNumber(
              Number(
                formatNumber(Number(abaList[i]?.total_amount)).replace(
                  /,/g,
                  "",
                ) / 1.1,
              ),
            )
          : `$0.00`,
        amount: abaList[i]?.amount
          ? "$" + formatNumber(Number(abaList[i]?.amount))
          : `$0.00`,
        total_amount: abaList[i]?.total_amount ? (
          <b>{`$${formatNumber(Number(abaList[i]?.total_amount))}`}</b>
        ) : (
          <b>{"$0.00"}</b>
        ),

        total_amount_exc: abaList[i]?.total_amount_exc ? (
          <b>{`$${formatNumber(Number(abaList[i]?.total_amount_exc))}`}</b>
        ) : (
          <b>{"$0.00"}</b>
        ),
        total_tax_amount: abaList[i]?.total_tax_amount ? (
          <b>{`$${formatNumber(Number(abaList[i]?.total_tax_amount))}`}</b>
        ) : (
          <b>{"$0.00"}</b>
        ),
        fees: abaList[i]?.fees
          ? "$" + formatNumber(Number(abaList[i]?.fees))
          : `$0.00`,

        file: abaList[i]?.file
          ? `${abaList[i]?.file?.split("/").slice(-1)}`
          : "",
        payments: abaList[i]?.payment ? abaList[i]?.payment : "",
        is_reversed: abaList[i]?.is_reversed,
        is_refund: abaList[i]?.is_refund ? abaList[i]?.is_refund : "",
        action: (
          <>
            <DownloadABAActionButton
              trustId={trustId}
              item={abaList[i]}
              id={abaList[i]?.id}
              is_reversed={abaList[i]?.is_reversed}
              is_refund={abaList[i]?.is_refund}
              fetchAbas={fetchAbas}
            />
          </>
        ),
      });
    }
    return items;
  };

  const abas = abasGenerator(abaList?.length);

  const columns = [
    {
      headerClasses: THClasses,
      classes: TDClasses,
      dataField: "transaction_date",
      text: "Transaction Date",
      sort: true,
    },
    {
      headerClasses: THClasses,
      classes: TDClasses,
      dataField: "name",
      text: "Payee",
    },
    {
      headerClasses: THClasses,
      classes: TDClasses,
      dataField: "bank_name",
      text: "Bank Name",
    },
    {
      headerClasses: THClasses,
      classes: TDClasses,
      dataField: "description",
      text: "Description",
    },
    // {
    //   headerClasses: THClasses,
    //   classes: TDClasses,
    //   dataField: "amount",
    //   text: "Amount",
    // },
    {
      headerClasses: THClasses,
      classes: TDClasses,
      dataField: "fees",
      text: "Fees",
    },
    {
      headerClasses: THClasses,
      classes: TDClasses,
      dataField: "total_amount",
      text: "Total Amount (Inc)",
    },
    {
      headerClasses: THClasses,
      classes: TDClasses,
      dataField: "total_amount_exc",
      text: "Total Amount (Exc)",
    },
    {
      headerClasses: THClasses,
      classes: TDClasses,
      dataField: "total_tax_amount",
      text: "Total Tax",
    },
    // {
    //   headerClasses: THClasses,
    //   classes: TDClasses,
    //   dataField: "aba_file",
    //   text: ".ABA File",
    // },
    {
      headerClasses: THClasses,
      classes: TDClasses,
      text: "Action",
      dataField: "action",
      width: "5%",
      className: "centeralign",
      align: "center",
      headerAlign: "center",
    },
  ];

  const handleTableChange = (type, { page, sortField, sortOrder }) => {
    if (type === "sort") {
      setPage(1);
      setSortField(sortField);
      setSortOrder(
        sortOrder === "asc" ? "ASC" : sortOrder === "desc" && "DESC",
      );
    }
  };

  useEffect(() => {
    navigate(
      `?tab=download-aba&limit=${limit ? limit : defaultLimitPagination}&page=${
        page ? page : 1
      }&search=${searchParam ? searchParam : ""}&sort_column=${
        sortField ? sortField : ""
      }&sort_order=${sortOrder ? sortOrder : ""}`,
      {
        replace: true,
      },
    );
  }, [navigate, limit, searchParam, page, sortField, sortOrder]);

  useEffect(() => {
    fetchAbas();
  }, [fetchAbas]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSave = useCallback(
    debounce((nextValue) => {
      setSearchParam(nextValue);
      setPage(1);
    }, 300),
    [],
  );

  const handleSearchChange = (event) => {
    const { value: nextValue } = event.target;
    setSearchParamData(nextValue);
    setPage(1);
    debouncedSave(nextValue);
  };

  const handleClearSearch = () => {
    setSearchParamData();
    setSearchParam("");
  };

  const handleUpdateLimit = (e) => {
    const limit = e.target.value;
    setLimit(limit);
    setPage(1);
    fetchAbas(limit, 1);
  };

  const handleChangePage = (page) => {
    setPage(page);
    fetchAbas(limit, page);
  };

  const expandRow = {
    renderer: (row) => (
      <>
        <Payments
          expandList={true}
          paymentData={row?.payments}
          abaList={true}
          expandedIds={row?.payments?.map((row) => row.id)}
        />
      </>
    ),
    onlyOneExpanding: true,
    showExpandColumn: true,
    expandByColumnOnly: true,
    headerClasses: THClasses,
    expandColumnRenderer: ({ expanded }) => {
      if (expanded) {
        return <CaretDownIcon />;
      }
      return <CaretRightIcon />;
    },
    className: TDClasses,
    expandHeaderColumnRenderer: () => {
      return <></>;
    },
  };

  return (
    <>
      <MainBody
        searchParamData={searchParamData}
        handleClearSearch={handleClearSearch}
        handleSearchChange={handleSearchChange}
        innerBody={true}
        parentHeight={true}
        mainClassName="w-[calc(100%-3rem)]"
        actionContent={<></>}
        loading={loading}
      >
        <div className="custom-table">
          <div className="table-body-class sm:max-h-[640px]">
            <BootstrapTable
              keyField="key"
              remote
              data={abas}
              columns={columns}
              expandRow={expandRow}
              onTableChange={handleTableChange}
              rowClasses={(row) => {
                if (row?.is_reversed) {
                  return `${TRClasses} bg-muted dark:text-gray-400 text-gray-600 dark:bg-muted/50`;
                }

                if (row?.is_refund) {
                  return `${TRClasses} bg-muted dark:text-gray-400 text-gray-600 dark:bg-muted/50`;
                }

                return TRClasses;
              }}
              noDataIndication="No Data Found"
              classes={TableClasses}
              wrapperClasses={TableWrapClasses}
            />
          </div>

          <Pagination
            total={total}
            limit={parseInt(limit)}
            currentPage={page}
            updateLimit={handleUpdateLimit}
            updatePage={handleChangePage}
            from={from}
            to={to}
          />
        </div>
      </MainBody>
    </>
  );
};

export default DownloadedABA;
